<template>
  <div class="edlm" :class="{ 'edlm--selecting': selectionMode }" dir="rtl">
    <div class="edlm__header">
      <div class="edlm__bar">
        <div class="edlm__bar-actions">
          <button
            type="button"
            class="edlm__bar-btn edlm__bar-btn--create"
            :aria-label="$t(entrepotDocsList.type)"
            @click="goToNew"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            type="button"
            class="edlm__bar-btn"
            :aria-label="$t('refresh')"
            @click="onRefreshTap"
          >
            <v-icon size="20" color="#37474F">mdi-refresh</v-icon>
          </button>
        </div>
        <h1 class="edlm__title">{{ $t(entrepotDocsList.type) }}</h1>
        <button
          type="button"
          class="edlm__bar-btn edlm__bar-btn--back"
          :aria-label="$t('back')"
          @click="goBack"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>

      <div class="edlm__search-row">
        <div class="edlm__search">
          <v-icon size="20" color="#90A4AE" class="edlm__search-icon">
            mdi-magnify
          </v-icon>
          <input
            v-model="entrepotDocsList.search"
            type="search"
            class="edlm__search-input"
            :placeholder="$t('search')"
            autocomplete="off"
          />
          <button
            v-if="entrepotDocsList.search"
            type="button"
            class="edlm__search-clear"
            :aria-label="$t('clear')"
            @click="entrepotDocsList.search = ''"
          >
            <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
          </button>
        </div>
        <button
          type="button"
          class="edlm__filter-btn"
          :class="{ 'edlm__filter-btn--on': hasAnyFilter }"
          :aria-label="$t('filter')"
          @click="onFilterTap"
        >
          <v-icon
            size="22"
            :color="hasAnyFilter ? '#1565C0' : '#546E7A'"
          >
            mdi-filter-outline
          </v-icon>
          <span v-if="hasAnyFilter" class="edlm__filter-dot" />
        </button>
        <button
          type="button"
          class="edlm__filter-btn"
          :class="{ 'edlm__filter-btn--on': selectionMode }"
          :aria-label="$t('select')"
          @click="toggleSelectionMode"
        >
          <v-icon size="22" :color="selectionMode ? '#1565C0' : '#546E7A'">
            mdi-checkbox-multiple-marked-outline
          </v-icon>
        </button>
      </div>

      <div v-if="selectionMode" class="edlm__select-bar" dir="rtl">
        <button
          type="button"
          class="edlm__select-bar-btn"
          @click="selectAllLoaded"
        >
          <v-icon size="17" color="#5B7C99">mdi-check-all</v-icon>
          {{ $t("selectAllItems") }}
        </button>
        <span class="edlm__select-count">
          {{ selectedItems.length }} {{ $t("selectedCount") }}
        </span>
        <button
          type="button"
          class="edlm__select-bar-btn"
          @click="clearSelection"
        >
          <v-icon size="17" color="#90A4AE">mdi-close</v-icon>
          {{ $t("deselectAll") }}
        </button>
      </div>
    </div>

    <div
      v-if="entrepotDocsList.loading && !displayItems.length"
      class="edlm__state"
    >
      <v-progress-circular indeterminate color="#5B7C99" size="28" />
    </div>
    <div
      v-else-if="!displayItems.length"
      class="edlm__state edlm__state--empty"
    >
      <img
        class="edlm__empty-logo"
        src="@/assets/dashboard/no-data-cloud.png"
        alt=""
      />
      <p class="edlm__empty-text">{{ $t("no-data") }}</p>
    </div>
    <div v-else class="edlm__list">
      <article
        v-for="(item, index) in displayItems"
        :key="item.id || index"
        class="edlm-card"
        :class="{
          'edlm-card--selected': selectionMode && isSelected(item),
        }"
      >
        <div
          v-if="selectionMode"
          class="edlm-card__select-overlay"
          :class="{ 'edlm-card__select-overlay--on': isSelected(item) }"
          @click="toggleSelect(item)"
        >
          <span v-if="isSelected(item)" class="edlm-card__check">
            <v-icon size="17" color="#FFFFFF">mdi-check</v-icon>
          </span>
        </div>
        <div class="edlm-card__head">
          <div class="edlm-card__title-block">
            <button
              type="button"
              class="edlm-card__name-link"
              :disabled="!!item.lock"
              @click="entrepotDocsList.edit(item)"
            >
              <span class="edlm-card__name">
                {{ docNumber(item) }}
              </span>
              <v-icon
                v-if="item.lock"
                size="14"
                color="#E53935"
                class="ms-1"
              >
                mdi-lock-outline
              </v-icon>
              <v-icon v-else size="12" color="#879EB6">mdi-pencil-outline</v-icon>
            </button>
            <div class="edlm-card__sub">
              {{ $t("entrepot") }}:
              {{ item.entrepot && item.entrepot.name ? item.entrepot.name : "—" }}
              <template v-if="isTransfer">
                · {{ $t("recipientEntrepot") }}:
                {{
                  item.recipient_entrepot && item.recipient_entrepot.name
                    ? item.recipient_entrepot.name
                    : "—"
                }}
              </template>
            </div>
          </div>
          <span class="edlm-card__number">#{{ item.id }}</span>
        </div>

        <div class="edlm-card__amounts">
          <div v-if="isTransfer && item.type" class="edlm-card__amount-row">
            <span class="edlm-card__amount-label">{{ $t("type") }}</span>
            <span class="edlm-card__amount-value">{{ $t(item.type) }}</span>
          </div>
          <div class="edlm-card__amount-row">
            <span class="edlm-card__amount-label">{{ $t("date") }}</span>
            <span class="edlm-card__amount-value">{{ item.date || "—" }}</span>
          </div>
          <div class="edlm-card__amount-row">
            <span class="edlm-card__amount-label">{{ $t("branch") }}</span>
            <span class="edlm-card__amount-value">
              {{ item.branch && item.branch.name ? item.branch.name : "—" }}
            </span>
          </div>
          <div class="edlm-card__amount-row">
            <span class="edlm-card__amount-label">{{ $t("status") }}</span>
            <span
              class="edlm-card__status-pill"
              :class="{
                'edlm-card__status-pill--empty': !hasLastStatus(item),
              }"
              :style="lastStatusStyle(item)"
            >
              {{ lastStatusLabel(item) }}
            </span>
          </div>
          <div v-if="item.status" class="edlm-card__amount-row">
            <span class="edlm-card__amount-label">{{ $t("statusControll") }}</span>
            <span
              class="edlm-card__status-pill"
              :style="controllStatusStyle(item)"
            >
              {{ $t(item.status) }}
            </span>
          </div>
        </div>

        <div v-if="!selectionMode" class="edlm-card__footer">
          <div class="edlm-card__actions">
            <button
              v-show="canRemove"
              type="button"
              class="edlm-btn edlm-btn--danger"
              :disabled="!!item.lock"
              @click="askDelete(item)"
            >
              <v-icon size="14" color="#E53935">mdi-trash-can-outline</v-icon>
              {{ $t("remove") }}
            </button>
            <button
              type="button"
              class="edlm-btn edlm-btn--primary"
              :disabled="!!item.lock"
              @click="entrepotDocsList.edit(item)"
            >
              <v-icon size="14" color="#FFFFFF">mdi-pencil-outline</v-icon>
              {{ $t("edit") }}
            </button>
            <button
              type="button"
              class="edlm-btn edlm-btn--outline"
              @click="openDetails(item)"
            >
              <v-icon size="14" color="#5B7C99">mdi-eye-outline</v-icon>
              {{ $t("details") }}
            </button>
          </div>
          <button
            type="button"
            class="edlm-rel-btn"
            :disabled="!hasRelation(item)"
            @click="openRelationsSheet(item)"
          >
            <v-icon
              size="14"
              :color="hasRelation(item) ? '#5B7C99' : '#90A4AE'"
            >
              mdi-link-variant
            </v-icon>
            {{ $t("relations") }}
          </button>
        </div>
      </article>
      <div v-if="appending" class="edlm__state edlm__state--more">
        <v-progress-circular indeterminate color="#5B7C99" size="22" />
      </div>
    </div>

    <div ref="sentinel" class="edlm__sentinel" />

    <div v-if="selectionMode" class="edlm-bulkbar" dir="rtl">
      <button
        type="button"
        class="edlm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="entrepotDocsList.print(null, 'doc', selectedItems)"
      >
        <v-icon size="19" color="#5B7C99">mdi-printer-outline</v-icon>
        {{ $t("groupPrint") }}
      </button>
      <button
        v-if="entrepotDocsList.canLabelPrint"
        type="button"
        class="edlm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="entrepotDocsList.labelPrint(null, selectedItems)"
      >
        <v-icon size="19" color="#5B7C99">mdi-truck-delivery-outline</v-icon>
        {{ $t("entrepotLabelPrint") }}
      </button>
      <button
        v-if="lockPermission"
        type="button"
        class="edlm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('lock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-outline</v-icon>
        {{ $t("lock") }}
      </button>
      <button
        v-if="lockPermission"
        type="button"
        class="edlm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('unlock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-open-outline</v-icon>
        {{ $t("unlock") }}
      </button>
      <button
        v-if="canRemove"
        type="button"
        class="edlm-bulkbar__btn edlm-bulkbar__btn--danger"
        :disabled="!selectedItems.length"
        @click="askGroupDelete"
      >
        <v-icon size="19" color="#E53935">mdi-trash-can-outline</v-icon>
        {{ $t("groupDelete") }}
      </button>
    </div>

    <v-bottom-sheet v-model="detailSheet" scrollable>
      <v-card class="edlm-sheet">
        <div class="edlm-sheet__handle" />
        <div class="edlm-sheet__header" dir="rtl">
          <div class="edlm-sheet__header-side">
            <span v-if="detailItem" class="edlm-sheet__num-chip">
              #{{ detailItem.id }}
            </span>
          </div>
          <div class="edlm-sheet__title">{{ $t("details") }}</div>
          <div class="edlm-sheet__header-side edlm-sheet__header-side--end">
            <button
              type="button"
              class="edlm-sheet__back"
              :aria-label="$t('back')"
              @click="detailSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <div v-if="detailItem" class="edlm-sheet__quickbar" dir="rtl">
          <button
            type="button"
            class="edlm-sheet__quick"
            :disabled="!!detailItem.lock"
            @click="runNamedAction('edit')"
          >
            <v-icon size="18" color="#5B7C99">mdi-pencil-outline</v-icon>
            {{ $t("edit") }}
          </button>
          <button
            type="button"
            class="edlm-sheet__quick"
            @click="runNamedAction('print')"
          >
            <v-icon size="18" color="#5B7C99">mdi-printer-outline</v-icon>
            {{ $t("print") }}
          </button>
          <button
            v-if="entrepotDocsList.canLabelPrint"
            type="button"
            class="edlm-sheet__quick"
            @click="runNamedAction('label')"
          >
            <v-icon size="18" color="#5B7C99">mdi-truck-delivery-outline</v-icon>
            {{ $t("entrepotLabelPrint") }}
          </button>
          <button
            v-if="detailItem.doc_head_id || (detailItem.doc_header && detailItem.doc_header.id)"
            type="button"
            class="edlm-sheet__quick"
            @click="runNamedAction('viewDoc')"
          >
            <v-icon size="18" color="#5B7C99">mdi-file-eye-outline</v-icon>
            {{ $t("viewAccountingDoc") }}
          </button>
          <button
            v-if="lockPermission"
            type="button"
            class="edlm-sheet__quick"
            @click="runNamedAction('lock')"
          >
            <v-icon
              size="18"
              :color="detailItem.lock ? 'green' : 'pink'"
            >
              {{
                detailItem.lock
                  ? "mdi-lock-open-outline"
                  : "mdi-lock-outline"
              }}
            </v-icon>
            {{ detailItem.lock ? $t("unlock") : $t("lock") }}
          </button>
          <button
            type="button"
            class="edlm-sheet__quick"
            @click="moreSheet = true"
          >
            <v-icon size="18" color="#5B7C99">mdi-dots-horizontal</v-icon>
            {{ $t("more") }}
          </button>
        </div>

        <div v-if="detailItem" class="edlm-sheet__name-block" dir="rtl">
          <div class="edlm-sheet__name-row">
            <span class="edlm-sheet__row-label">{{ $t("document-number") }}</span>
            <span class="edlm-sheet__name-value">{{ docNumber(detailItem) }}</span>
          </div>
          <button
            v-if="canCheckout(detailItem)"
            type="button"
            class="edlm-sheet__linkrow"
            @click="runNamedAction('checkout')"
          >
            <v-icon size="18" color="#00796B">mdi-tag-check</v-icon>
            <span class="edlm-sheet__linkrow-text">
              {{ $t(entrepotDocsList.type + "Checkout") }}
            </span>
            <v-icon size="18" color="#80CBC4">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div v-if="detailItem" class="edlm-sheet__body" dir="rtl">
          <div class="edlm-sheet__card">
            <div
              v-for="(row, i) in detailRows(detailItem)"
              :key="'detail-row-' + i"
              class="edlm-sheet__row"
              :class="{ 'edlm-sheet__row--band': row.kind === 'band' }"
            >
              <span class="edlm-sheet__row-label">{{ row.label }}</span>
              <span class="edlm-sheet__row-value">{{ row.value }}</span>
            </div>

            <div class="edlm-sheet__row">
              <span class="edlm-sheet__row-label">{{ $t("status") }}</span>
              <span
                v-if="hasLastStatus(detailItem)"
                class="edlm-card__status-pill"
                :style="lastStatusStyle(detailItem)"
              >
                {{ lastStatusLabel(detailItem) }}
              </span>
              <span v-else class="edlm-sheet__row-value">—</span>
            </div>

            <div class="edlm-sheet__row">
              <span class="edlm-sheet__row-label">{{ $t("relation-whit") }}</span>
              <span v-if="detailItem.invoice" class="edlm-sheet__tags">
                <v-chip
                  :color="detailItem.invoice.color || '#607D8B'"
                  label
                  outlined
                  small
                  class="px-1"
                >
                  {{ detailItem.invoice.name }}
                </v-chip>
              </span>
              <span v-else class="edlm-sheet__row-value">—</span>
            </div>

            <div class="edlm-sheet__row">
              <span class="edlm-sheet__row-label">{{ $t("tag") }}</span>
              <span
                v-if="detailItem.tags && detailItem.tags.length"
                class="edlm-sheet__tags"
              >
                <v-chip
                  v-for="(tag, ti) in detailItem.tags"
                  :key="'detail-tag-' + ti"
                  :color="tag.color || '#607D8B'"
                  label
                  small
                  class="px-1"
                >
                  <span class="px-1 white--text">{{ tag.text }}</span>
                </v-chip>
              </span>
              <span v-else class="edlm-sheet__row-value">—</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="moreSheet">
      <v-card class="edlm-sheet">
        <div class="edlm-sheet__handle" />
        <div class="edlm-sheet__header" dir="rtl">
          <span class="edlm-sheet__header-spacer" />
          <div class="edlm-sheet__title">{{ $t("more") }}</div>
          <button
            type="button"
            class="edlm-sheet__back"
            :aria-label="$t('back')"
            @click="moreSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div v-if="detailItem" class="edlm-sheet__body" dir="rtl">
          <div class="edlm-sheet__actions edlm-sheet__actions--sheet">
            <button
              v-if="canCheckout(detailItem)"
              type="button"
              class="edlm-sheet__action"
              @click="runNamedAction('checkout')"
            >
              <v-icon size="20" color="#5B7C99">mdi-tag-check</v-icon>
              <span>{{ $t(entrepotDocsList.type + "Checkout") }}</span>
            </button>
            <button
              type="button"
              class="edlm-sheet__action"
              @click="runNamedAction('copyToInvoice')"
            >
              <v-icon size="20" color="#5B7C99">mdi-content-copy</v-icon>
              <span>{{ $t("copyToInvoice") }}</span>
            </button>
            <button
              v-if="isWarehouseHandling"
              type="button"
              class="edlm-sheet__action"
              @click="runNamedAction('excel')"
            >
              <v-icon size="20" color="#00897B">mdi-file-excel-outline</v-icon>
              <span>{{ $t("get-excel") }}</span>
            </button>
            <button
              type="button"
              class="edlm-sheet__action"
              v-clipboard:copy="copyLink(detailItem)"
              @click="onCopyLinkQuick"
            >
              <v-icon size="20" color="#5B7C99">mdi-link</v-icon>
              <span>{{ $t("copy-link") }}</span>
            </button>
            <div class="edlm-sheet__action edlm-sheet__action--log">
              <log
                :title-prop="$t('history')"
                icon-prop="mdi-history"
                :listProp="true"
                :type-prop="entrepotDocsList.type"
                :refIdProp="detailItem.id"
                mode-prop="button"
              />
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Layered filter: same shell fields as desktop. -->
    <v-bottom-sheet v-model="filterSheet" scrollable>
      <v-card class="edlm-sheet edlm-sheet--filter">
        <div class="edlm-sheet__handle" />
        <div class="edlm-sheet__header" dir="rtl">
          <span class="edlm-sheet__header-spacer" />
          <div class="edlm-sheet__title">{{ $t("filter") }}</div>
          <button
            type="button"
            class="edlm-sheet__back"
            :aria-label="$t('back')"
            @click="filterSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="edlm-filter-nav" dir="rtl">
          <button
            v-for="group in filterGroups"
            :key="group.key"
            type="button"
            class="edlm-filter-nav__item"
            @click="openFilterGroup(group.key)"
          >
            <v-icon class="edlm-filter-nav__icon" size="22" color="#5B7C99">
              {{ group.icon }}
            </v-icon>
            <span class="edlm-filter-nav__texts">
              <span class="edlm-filter-nav__item-title">{{ group.title }}</span>
              <span class="edlm-filter-nav__item-sub">
                {{ groupSubtitle(group.key) }}
              </span>
            </span>
            <span v-if="groupHasValue(group.key)" class="edlm-filter-nav__dot" />
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div class="edlm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="edlm-sheet__btn edlm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="edlm-sheet__btn edlm-sheet__btn--ghost"
            @click="clearFilter"
          >
            <v-icon size="16" color="#5B7C99">mdi-filter-remove-outline</v-icon>
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="filterGroupSheet" scrollable>
      <v-card class="edlm-sheet edlm-sheet--filter">
        <div class="edlm-sheet__handle" />
        <div class="edlm-sheet__header" dir="rtl">
          <span class="edlm-sheet__header-spacer" />
          <div class="edlm-sheet__title">{{ activeFilterGroupTitle }}</div>
          <button
            type="button"
            class="edlm-sheet__back"
            :aria-label="$t('back')"
            @click="backToFilterMain"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="edlm-filter-fields" dir="rtl">
          <template v-if="filterGroup === 'entities'">
            <div class="edlm-f-field-wrap">
              <div class="edlm-f-field-wrap__label">{{ $t("product") }}</div>
              <button
                type="button"
                class="edlm-f-picker-field"
                @click="openPicker('edProduct')"
              >
                <span class="edlm-f-picker-field__text">
                  {{ pickerSummary("edProduct", entrepotDocsList.product) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="edlm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="edProduct"
                  v-model="entrepotDocsList.product"
                  type="product"
                  :title="$t('product')"
                  :outlined="true"
                />
              </div>
            </div>
            <div class="edlm-f-field-wrap">
              <div class="edlm-f-field-wrap__label">{{ $t("entrepot") }}</div>
              <button
                type="button"
                class="edlm-f-picker-field"
                @click="openPicker('edEntrepot')"
              >
                <span class="edlm-f-picker-field__text">
                  {{ pickerSummary("edEntrepot", entrepotDocsList.entrepot) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="edlm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="edEntrepot"
                  v-model="entrepotDocsList.entrepot"
                  title="entrepot"
                  type="entrepot"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="edlm-f-field-wrap">
              <div class="edlm-f-field-wrap__label">{{ $t("branch") }}</div>
              <button
                type="button"
                class="edlm-f-picker-field"
                @click="openPicker('edBranch')"
              >
                <span class="edlm-f-picker-field__text">
                  {{ pickerSummary("edBranch", entrepotDocsList.branch) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="edlm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="edBranch"
                  v-model="entrepotDocsList.branch"
                  title="branch"
                  type="branch"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'time'">
            <div
              v-for="field in timeFields"
              :key="field.key"
              class="edlm-f-field-wrap"
            >
              <div class="edlm-f-field-wrap__label">{{ field.label }}</div>
              <button
                type="button"
                class="edlm-f-picker-field"
                @click="openTimeLayer(field)"
              >
                <span class="edlm-f-picker-field__text">
                  {{
                    hasValue(entrepotDocsList[field.key])
                      ? entrepotDocsList[field.key]
                      : "—"
                  }}
                </span>
                <span
                  v-if="hasValue(entrepotDocsList[field.key])"
                  class="edlm-picker-cover__clear"
                  @click.stop="entrepotDocsList[field.key] = null"
                >
                  <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>

          <template v-else-if="filterGroup === 'category'">
            <div class="edlm-f-field-wrap">
              <div class="edlm-f-field-wrap__label">{{ $t("tag") }}</div>
              <button
                type="button"
                class="edlm-f-picker-field"
                @click="openPicker('edTag')"
              >
                <span class="edlm-f-picker-field__text">
                  {{ pickerSummary("edTag", entrepotDocsList.tag) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="edlm-picker-host" aria-hidden="true">
                <tag ref="edTag" v-model="entrepotDocsList.tag" />
              </div>
            </div>
            <div class="edlm-f-field-wrap">
              <div class="edlm-f-field-wrap__label">{{ $t("status") }}</div>
              <div class="edlm-picker-overlay">
                <status
                  ref="edStatus"
                  class="edlm-f-field"
                  v-model="entrepotDocsList.sellStatus"
                  :hideconfig="true"
                />
                <button
                  type="button"
                  class="edlm-f-picker-field edlm-picker-cover"
                  @click="openStatusPicker"
                >
                  <span class="edlm-f-picker-field__text">
                    {{ statusSummary() }}
                  </span>
                  <span
                    v-if="hasValue(entrepotDocsList.sellStatus)"
                    class="edlm-picker-cover__clear"
                    @click.stop="entrepotDocsList.sellStatus = null"
                  >
                    <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                  </span>
                  <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
                </button>
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'status'">
            <div class="edlm-f-field-wrap">
              <div class="edlm-f-field-wrap__label">
                {{ $t("lockStatusDocuments") }}
              </div>
              <button
                type="button"
                class="edlm-f-picker-field"
                @click="
                  openSelectLayer(
                    'lockStatus',
                    $t('lockStatusDocuments'),
                    entrepotDocsList.lockStatusItems || [],
                    'text',
                    false
                  )
                "
              >
                <span class="edlm-f-picker-field__text">
                  {{
                    selectSummary(
                      "lockStatus",
                      entrepotDocsList.lockStatusItems || [],
                      "text"
                    )
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
            <div class="edlm-f-field-wrap">
              <div class="edlm-f-field-wrap__label">{{ $t("statusControll") }}</div>
              <button
                type="button"
                class="edlm-f-picker-field"
                @click="
                  openSelectLayer(
                    'statusControll',
                    $t('statusControll'),
                    entrepotDocsList.statusItems || [],
                    'text',
                    false
                  )
                "
              >
                <span class="edlm-f-picker-field__text">
                  {{
                    selectSummary(
                      "statusControll",
                      entrepotDocsList.statusItems || [],
                      "text"
                    )
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>
        </div>

        <div class="edlm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="edlm-sheet__btn edlm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="edlm-sheet__btn edlm-sheet__btn--ghost"
            @click="backToFilterMain"
          >
            {{ $t("back") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="timeLayerOpen">
      <v-card class="edlm-sheet edlm-sheet--filter">
        <div class="edlm-sheet__handle" />
        <div class="edlm-sheet__header" dir="rtl">
          <span class="edlm-sheet__header-spacer" />
          <div class="edlm-sheet__title">{{ timeLayerLabel }}</div>
          <button
            type="button"
            class="edlm-sheet__back"
            :aria-label="$t('back')"
            @click="timeLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="edlm-mobile-date" dir="rtl">
          <div class="edlm-mobile-date__selected">
            <span class="edlm-mobile-date__label">{{ timeLayerLabel }}</span>
            <strong class="edlm-mobile-date__value">
              {{
                hasValue(entrepotDocsList[timeLayerField])
                  ? entrepotDocsList[timeLayerField]
                  : "—"
              }}
            </strong>
            <button
              v-if="hasValue(entrepotDocsList[timeLayerField])"
              type="button"
              class="edlm-mobile-date__clear"
              @click="entrepotDocsList[timeLayerField] = null"
            >
              {{ $t("clear") }}
            </button>
          </div>
          <date-picker
            v-if="timeLayerField"
            :key="timeLayerField"
            v-model="entrepotDocsList[timeLayerField]"
            inline
            simple
            auto-submit
            type="time"
            :locale="$store.state.date_locale"
            :format="'HH:mm'"
            :display-format="'HH'"
            class="edlm-mobile-date__picker"
            color="#7B8BA3"
          />
        </div>

        <div class="edlm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="edlm-sheet__btn edlm-sheet__btn--primary"
            @click="timeLayerOpen = false"
          >
            {{ $t("Save") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="selectLayerOpen">
      <v-card class="edlm-sheet edlm-sheet--filter">
        <div class="edlm-sheet__handle" />
        <div class="edlm-sheet__header" dir="rtl">
          <span class="edlm-sheet__header-spacer" />
          <div class="edlm-sheet__title">{{ selectLayerTitle }}</div>
          <button
            type="button"
            class="edlm-sheet__back"
            :aria-label="$t('back')"
            @click="selectLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="edlm-select-layer" dir="rtl">
          <button
            v-for="item in selectLayerItems"
            :key="item.value"
            type="button"
            class="edlm-select-layer__item"
            @click="selectLayerPick(item.value)"
          >
            <span class="edlm-select-layer__text">
              {{ item[selectLayerTextKey] }}
            </span>
            <v-icon
              v-if="isSelectLayerSelected(item.value)"
              size="20"
              color="#5B7C99"
            >
              mdi-check
            </v-icon>
          </button>
          <button
            v-if="
              selectLayerField && hasValue(entrepotDocsList[selectLayerField])
            "
            type="button"
            class="edlm-select-layer__clear"
            @click="selectLayerClear"
          >
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Relations layer: desktop invoice chip content -->
    <v-bottom-sheet v-model="relationsSheet" scrollable>
      <v-card class="edlm-sheet">
        <div class="edlm-sheet__handle" />
        <div class="edlm-sheet__header" dir="rtl">
          <div class="edlm-sheet__header-side">
            <span v-if="relationsItem" class="edlm-sheet__num-chip">
              #{{ relationsItem.id }}
            </span>
          </div>
          <div class="edlm-sheet__title">{{ $t("relations") }}</div>
          <div class="edlm-sheet__header-side edlm-sheet__header-side--end">
            <button
              type="button"
              class="edlm-sheet__back"
              :aria-label="$t('back')"
              @click="relationsSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <div v-if="relationsItem" class="edlm-sheet__body" dir="rtl">
          <div v-if="!hasRelation(relationsItem)" class="edlm-rel-empty">
            —
          </div>
          <div v-else class="edlm-rel-item">
            <div class="edlm-rel-item__title">
              {{ activeInvoiceRelation.typeLabel }}
            </div>
            <div
              v-for="(line, li) in activeInvoiceRelation.lines"
              :key="'rel-line-' + li"
              class="edlm-rel-item__line"
            >
              <span class="edlm-rel-item__key">{{ line.label }}</span>
              <span class="edlm-rel-item__val">{{ line.value }}</span>
            </div>
          </div>
        </div>

        <div class="edlm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="edlm-sheet__btn edlm-sheet__btn--primary"
            @click="relationsSheet = false"
          >
            {{ $t("back") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="deleteSheet">
      <v-card class="edlm-confirm-sheet" dir="rtl">
        <div class="edlm-confirm-sheet__handle" />
        <div class="edlm-confirm-sheet__icon">
          <v-icon size="26" color="#E53935">mdi-trash-can-outline</v-icon>
        </div>
        <div class="edlm-confirm-sheet__title">
          {{ deleteGroupMode ? $t("groupDelete") : $t("delete") }}
        </div>
        <p class="edlm-confirm-sheet__subtitle">
          {{ $t("are-you-sure-delete-this-case") }}
        </p>
        <div v-if="deleteGroupMode" class="edlm-confirm-sheet__rows">
          <div class="edlm-confirm-sheet__row">
            <span>{{ $t("totalCount") }}</span>
            <strong>{{ selectedItems.length }}</strong>
          </div>
          <div class="edlm-confirm-sheet__items">
            <div
              v-for="(sel, si) in selectedItems"
              :key="'del-item-' + si"
              class="edlm-confirm-sheet__item"
            >
              <span class="edlm-confirm-sheet__item-main">
                <span class="edlm-confirm-sheet__item-index">
                  {{ selectedRowNumber(sel) }}
                </span>
                <span class="edlm-confirm-sheet__item-name">
                  {{ docNumber(sel) }}
                </span>
              </span>
              <span class="edlm-confirm-sheet__item-number">#{{ sel.id }}</span>
            </div>
          </div>
        </div>
        <div
          v-else-if="entrepotDocsList.deleteItem"
          class="edlm-confirm-sheet__rows"
        >
          <div class="edlm-confirm-sheet__row">
            <span>{{ $t("document-number") }}</span>
            <strong>{{ docNumber(entrepotDocsList.deleteItem) }}</strong>
          </div>
          <div class="edlm-confirm-sheet__row">
            <span>{{ $t("entrepot") }}</span>
            <strong>
              {{
                entrepotDocsList.deleteItem.entrepot &&
                entrepotDocsList.deleteItem.entrepot.name
                  ? entrepotDocsList.deleteItem.entrepot.name
                  : "—"
              }}
            </strong>
          </div>
          <div class="edlm-confirm-sheet__row">
            <span>{{ $t("date") }}</span>
            <strong>{{ entrepotDocsList.deleteItem.date || "—" }}</strong>
          </div>
          <div class="edlm-confirm-sheet__row">
            <span>{{ $t("id") }}</span>
            <strong>{{ entrepotDocsList.deleteItem.id }}</strong>
          </div>
        </div>
        <div class="edlm-confirm-sheet__actions">
          <v-btn
            color="error"
            block
            depressed
            height="48"
            class="edlm-confirm-sheet__yes"
            @click="confirmDelete"
          >
            {{ $t("yes") }}
          </v-btn>
          <v-btn
            outlined
            block
            height="48"
            color="secondary"
            class="edlm-confirm-sheet__no"
            @click="cancelDelete"
          >
            {{ $t("no") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
export default {
  name: "EntrepotDocsListMobile",
  inject: ["entrepotDocsList"],
  components: {
    autocomplete: () =>
      import("../../../../components/general/patial/autocomplete"),
    Tag: () => import("../../../../components/general/Tag/tag"),
    Status: () => import("../../../../components/general/Status/status"),
  },
  data() {
    return {
      displayItems: [],
      appending: false,
      observer: null,
      detailSheet: false,
      moreSheet: false,
      detailItem: null,
      filterSheet: false,
      filterGroupSheet: false,
      filterGroup: null,
      timeLayerOpen: false,
      timeLayerField: null,
      timeLayerLabel: "",
      selectLayerOpen: false,
      selectLayerField: null,
      selectLayerTitle: "",
      selectLayerItems: [],
      selectLayerTextKey: "text",
      selectLayerMulti: false,
      selectionMode: false,
      selectedItems: [],
      _selectionSynced: false,
      deleteSheet: false,
      deleteGroupMode: false,
      relationsSheet: false,
      relationsItem: null,
    };
  },
  computed: {
    currentPage() {
      const opts = this.entrepotDocsList.tableOptions || {};
      return opts.page || 1;
    },
    lockPermission() {
      return !!this.entrepotDocsList.lockPermission;
    },
    canRemove() {
      const type = this.entrepotDocsList.type;
      if (!type) return false;
      if (typeof this.entrepotDocsList.removePermission === "function") {
        return !!this.entrepotDocsList.removePermission(type);
      }
      return false;
    },
    isTransfer() {
      return this.entrepotDocsList.type === "transferRemittance";
    },
    isWarehouseHandling() {
      return (
        this.entrepotDocsList.type === "warehouseHandling" ||
        this.$route.name === "warehouseHandlingList"
      );
    },
    hasAnyFilter() {
      return (
        this.groupHasValue("entities") ||
        this.groupHasValue("time") ||
        this.groupHasValue("category") ||
        this.groupHasValue("status")
      );
    },
    filterGroups() {
      return [
        {
          key: "entities",
          icon: "mdi-warehouse",
          title: this.$t("product") + " / " + this.$t("entrepot"),
        },
        {
          key: "time",
          icon: "mdi-clock-outline",
          title: this.$t("from-hour") + " / " + this.$t("to-hour"),
        },
        {
          key: "category",
          icon: "mdi-shape-outline",
          title: this.$t("tag") + " / " + this.$t("status"),
        },
        {
          key: "status",
          icon: "mdi-list-status",
          title: this.$t("statusControll"),
        },
      ];
    },
    activeFilterGroupTitle() {
      const group = this.filterGroups.find((g) => g.key === this.filterGroup);
      return group ? group.title : this.$t("filter");
    },
    timeFields() {
      return [
        { key: "fromTime", label: this.$t("from-hour") },
        { key: "toTime", label: this.$t("to-hour") },
      ];
    },
    activeInvoiceRelation() {
      return this.invoiceRelationRows(this.relationsItem);
    },
  },
  watch: {
    "entrepotDocsList.items"(val) {
      const items = val || [];
      if (this.appending) {
        const seen = {};
        this.displayItems.forEach((i) => {
          seen[i.id] = true;
        });
        this.displayItems = this.displayItems.concat(
          items.filter((i) => !seen[i.id])
        );
        this.appending = false;
      } else {
        this.displayItems = items.slice();
      }
    },
    "entrepotDocsList.search"() {
      this.appending = false;
      if (this.currentPage !== 1) {
        this.entrepotDocsList.tableOptions = Object.assign(
          {},
          this.entrepotDocsList.tableOptions,
          { page: 1 }
        );
      }
    },
    "entrepotDocsList.selected"(val) {
      if (!this.selectionMode || !this._selectionSynced) return;
      if (!val || !val.length) {
        this._selectionSynced = false;
        this.exitSelectionMode();
      }
    },
    deleteSheet(val) {
      if (!val) this.deleteGroupMode = false;
    },
  },
  mounted() {
    this.displayItems = (this.entrepotDocsList.items || []).slice();
    this.entrepotDocsList.tableOptions = Object.assign(
      {},
      this.entrepotDocsList.tableOptions,
      { page: 1, itemsPerPage: 10 }
    );
    this.$nextTick(() => {
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0] && entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ name: "Home" }).catch(() => {});
    },
    goToNew() {
      const type = this.entrepotDocsList.type;
      if (!type) return;
      if (typeof this.entrepotDocsList.goTo === "function") {
        this.entrepotDocsList.goTo(type);
      }
    },
    onRefreshTap() {
      this.appending = false;
      this.entrepotDocsList.tableOptions = Object.assign(
        {},
        this.entrepotDocsList.tableOptions,
        { page: 1 }
      );
      this.entrepotDocsList.getItems();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onFilterTap() {
      this.filterSheet = true;
    },
    openPicker(refName) {
      const comp = this.$refs[refName];
      if (comp && typeof comp.openMobileDialog === "function") {
        comp.openMobileDialog();
      }
    },
    openStatusPicker() {
      const comp = this.$refs.edStatus;
      const combo = comp && comp.$refs ? comp.$refs.combo : null;
      if (combo) {
        if (typeof combo.focus === "function") combo.focus();
        combo.isMenuActive = true;
      }
    },
    statusSummary() {
      const v = this.entrepotDocsList.sellStatus;
      if (!this.hasValue(v)) return "—";
      if (typeof v === "object") return v.name || v.text || "—";
      const comp = this.$refs.edStatus;
      const items = (comp && comp.items) || [];
      const found = items.find((i) => i && i.id === v);
      return found && found.name ? found.name : "—";
    },
    openSelectLayer(field, title, items, textKey, multi) {
      this.selectLayerField = field;
      this.selectLayerTitle = title;
      this.selectLayerItems = items || [];
      this.selectLayerTextKey = textKey || "text";
      this.selectLayerMulti = !!multi;
      this.selectLayerOpen = true;
    },
    isSelectLayerSelected(value) {
      if (!this.selectLayerField) return false;
      const current = this.entrepotDocsList[this.selectLayerField];
      if (this.selectLayerMulti) {
        return Array.isArray(current) && current.indexOf(value) !== -1;
      }
      return current === value;
    },
    selectLayerPick(value) {
      if (!this.selectLayerField) return;
      if (this.selectLayerMulti) {
        const current = Array.isArray(
          this.entrepotDocsList[this.selectLayerField]
        )
          ? this.entrepotDocsList[this.selectLayerField].slice()
          : [];
        const idx = current.indexOf(value);
        if (idx === -1) current.push(value);
        else current.splice(idx, 1);
        this.entrepotDocsList[this.selectLayerField] = current.length
          ? current
          : null;
        return;
      }
      this.entrepotDocsList[this.selectLayerField] = value;
      this.selectLayerOpen = false;
    },
    selectLayerClear() {
      if (this.selectLayerField) {
        this.entrepotDocsList[this.selectLayerField] = null;
      }
      this.selectLayerOpen = false;
    },
    selectSummary(field, items, textKey) {
      const v = this.entrepotDocsList[field];
      if (!this.hasValue(v)) return "—";
      const key = textKey || "text";
      if (Array.isArray(v)) {
        const names = [];
        v.forEach((val) => {
          const found = (items || []).find((i) => i && i.value === val);
          if (found) names.push(found[key]);
        });
        return names.length ? names.join(" · ") : String(v.length);
      }
      const found = (items || []).find((i) => i && i.value === v);
      return found ? found[key] : "—";
    },
    pickerSummary(refName, value) {
      if (!this.hasValue(value)) return "—";
      const comp = this.$refs[refName];
      const items = (comp && comp.items) || [];
      const arr = Array.isArray(value) ? value : [value];
      const names = [];
      arr.forEach((v) => {
        if (v && typeof v === "object") {
          if (v.name || v.text) names.push(v.name || v.text);
        } else {
          const found = items.find((i) => i && (i.id === v || i.value === v));
          if (found && (found.name || found.text)) {
            names.push(found.name || found.text);
          }
        }
      });
      if (names.length) return names.join(" · ");
      return String(arr.length);
    },
    openFilterGroup(key) {
      this.filterGroup = key;
      this.filterSheet = false;
      this.$nextTick(() => {
        this.filterGroupSheet = true;
      });
    },
    backToFilterMain() {
      this.filterGroupSheet = false;
      this.$nextTick(() => {
        this.filterSheet = true;
      });
    },
    hasValue(v) {
      if (Array.isArray(v)) return v.length > 0;
      return v !== null && v !== undefined && v !== "";
    },
    groupHasValue(key) {
      const list = this.entrepotDocsList;
      if (key === "entities") {
        return (
          this.hasValue(list.product) ||
          this.hasValue(list.entrepot) ||
          this.hasValue(list.branch)
        );
      }
      if (key === "time") {
        return this.hasValue(list.fromTime) || this.hasValue(list.toTime);
      }
      if (key === "category") {
        return this.hasValue(list.tag) || this.hasValue(list.sellStatus);
      }
      if (key === "status") {
        return (
          this.hasValue(list.lockStatus) || this.hasValue(list.statusControll)
        );
      }
      return false;
    },
    groupSubtitle(key) {
      const list = this.entrepotDocsList;
      if (key === "entities") {
        return (
          this.$t("product") +
          " · " +
          this.$t("entrepot") +
          " · " +
          this.$t("branch")
        );
      }
      if (key === "time") {
        const parts = [];
        if (this.hasValue(list.fromTime)) parts.push(list.fromTime);
        if (this.hasValue(list.toTime)) parts.push(list.toTime);
        return parts.length
          ? parts.join(" · ")
          : this.$t("from-hour") + " · " + this.$t("to-hour");
      }
      if (key === "category") {
        return this.$t("tag") + " · " + this.$t("status");
      }
      if (key === "status") {
        const parts = [];
        const lockItem = (list.lockStatusItems || []).find(
          (i) => i && i.value === list.lockStatus
        );
        if (lockItem) parts.push(lockItem.text);
        const ctrl = this.selectSummary(
          "statusControll",
          list.statusItems || [],
          "text"
        );
        if (ctrl !== "—") parts.push(ctrl);
        return parts.length ? parts.join(" · ") : this.$t("statusControll");
      }
      return "";
    },
    openTimeLayer(field) {
      this.timeLayerField = field.key;
      this.timeLayerLabel = field.label;
      this.timeLayerOpen = true;
    },
    applyFilter() {
      this.filterSheet = false;
      this.filterGroupSheet = false;
      this.selectLayerOpen = false;
      this.timeLayerOpen = false;
      this.appending = false;
      this.entrepotDocsList.tableOptions = Object.assign(
        {},
        this.entrepotDocsList.tableOptions,
        { page: 1 }
      );
      this.entrepotDocsList.getItems();
    },
    clearFilter() {
      const list = this.entrepotDocsList;
      list.product = null;
      list.entrepot = null;
      list.branch = null;
      list.fromTime = null;
      list.toTime = null;
      list.tag = null;
      list.sellStatus = null;
      list.lockStatus = null;
      list.statusControll = null;
      this.applyFilter();
    },
    loadMore() {
      const list = this.entrepotDocsList;
      if (list.loading || this.appending) return;
      const page = this.currentPage;
      const pageCount = list.pageCount || 1;
      if (page >= pageCount) return;
      this.appending = true;
      list.tableOptions = Object.assign({}, list.tableOptions, {
        page: page + 1,
      });
    },
    docNumber(item) {
      if (!item || !item.doc_header || !item.doc_header.number) return "—";
      return item.doc_header.number;
    },
    hasLastStatus(item) {
      return !!(
        item &&
        item.last_status &&
        item.last_status.status &&
        item.last_status.status.name
      );
    },
    lastStatusLabel(item) {
      if (!this.hasLastStatus(item)) return "—";
      return item.last_status.status.name;
    },
    lastStatusStyle(item) {
      if (!this.hasLastStatus(item)) return null;
      const color = item.last_status.status.color || "#5B7C99";
      return {
        background: color,
        color: "#FFFFFF",
        borderColor: color,
      };
    },
    controllStatusStyle(item) {
      const err = item && item.status === "storekeeperCountError";
      return {
        background: err ? "rgba(245, 124, 0, 0.14)" : "rgba(91, 124, 153, 0.12)",
        color: err ? "#E65100" : "#37474F",
        borderColor: err ? "rgba(245, 124, 0, 0.35)" : "rgba(91, 124, 153, 0.22)",
      };
    },
    canCheckout(item) {
      if (!item) return false;
      const type = this.entrepotDocsList.type;
      if (type !== "remittance" && type !== "receipt" && type !== "tempReceipt") {
        return false;
      }
      if (!item.status) return true;
      return !!this.$store.state.admin;
    },
    hasRelation(item) {
      return !!(item && item.invoice);
    },
    openRelationsSheet(item) {
      if (!this.hasRelation(item)) return;
      this.relationsItem = item;
      this.relationsSheet = true;
    },
    toLatinDigits(value) {
      return String(value == null ? "" : value)
        .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
        .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));
    },
    parseInvoiceName(name) {
      const text = String(name || "").trim();
      const latin = this.toLatinDigits(text);
      const result = {
        typeLabel: "",
        number: null,
        amount: null,
        account: null,
      };
      if (!text) return result;

      const accountMatch = text.match(
        /(?:طرف\s*حساب|account)\s*:?\s*(.+)$/i
      );
      if (accountMatch) {
        result.account = accountMatch[1].trim();
      }

      const amountMatch = latin.match(/(?:مبلغ|amount)\s*:?\s*([0-9,]+)/i);
      if (amountMatch) {
        result.amount = amountMatch[1].replace(/,/g, "");
      }

      const numMatch = latin.match(/(?:شماره|number)\s*:?\s*([0-9]+)/i);
      if (numMatch) {
        result.number = numMatch[1];
      }

      let typeLabel = text
        .replace(/(?:طرف\s*حساب|account)\s*:?\s*.+$/i, " ")
        .replace(/شماره\s*:?\s*[۰-۹0-9]+/gi, " ")
        .replace(/مبلغ\s*:?\s*[۰-۹0-9,،]+/gi, " ")
        .replace(/number\s*:?\s*[0-9]+/gi, " ")
        .replace(/amount\s*:?\s*[0-9,]+/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
      result.typeLabel = typeLabel;
      return result;
    },
    invoiceRelationRows(item) {
      const invoice = item && item.invoice ? item.invoice : null;
      const empty = { typeLabel: this.$t("relations"), lines: [] };
      if (!invoice) return empty;

      const parsed = this.parseInvoiceName(invoice.name);
      const typeLabel =
        parsed.typeLabel ||
        (invoice.type ? this.$t(invoice.type) : "") ||
        this.$t("relations");

      const lines = [];
      const number =
        parsed.number ||
        invoice.number ||
        invoice.refrence ||
        null;
      if (number != null && number !== "") {
        lines.push({ label: this.$t("number"), value: String(number) });
      }
      if (parsed.amount != null && parsed.amount !== "") {
        const amountText = this.$numberFormat
          ? this.$numberFormat(parseFloat(parsed.amount))
          : parsed.amount;
        lines.push({ label: this.$t("amount"), value: amountText });
      } else if (invoice.amount != null && invoice.amount !== "") {
        const amountText = this.$numberFormat
          ? this.$numberFormat(parseFloat(invoice.amount))
          : invoice.amount;
        lines.push({ label: this.$t("amount"), value: String(amountText) });
      }
      const account =
        parsed.account ||
        (invoice.customer && invoice.customer.name) ||
        null;
      if (account) {
        lines.push({ label: this.$t("account"), value: account });
      }
      if (invoice.id != null && invoice.id !== "") {
        lines.push({ label: this.$t("id"), value: String(invoice.id) });
      }
      if (!lines.length && invoice.name) {
        lines.push({ label: this.$t("document"), value: invoice.name });
      }
      return { typeLabel, lines };
    },
    openDetails(item) {
      this.detailItem = item;
      this.moreSheet = false;
      this.detailSheet = true;
    },
    askDelete(item) {
      this.moreSheet = false;
      this.detailSheet = false;
      this.deleteGroupMode = false;
      this.entrepotDocsList.selected = [];
      this._selectionSynced = false;
      this.entrepotDocsList.deleteItem = item;
      this.deleteSheet = true;
    },
    askGroupDelete() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.entrepotDocsList.deleteItem = null;
      this.deleteGroupMode = true;
      this.deleteSheet = true;
    },
    cancelDelete() {
      this.deleteSheet = false;
      this.deleteGroupMode = false;
    },
    confirmDelete() {
      this.deleteSheet = false;
      if (this.entrepotDocsList.selected && this.entrepotDocsList.selected.length) {
        this.entrepotDocsList.removegroup();
      } else {
        this.entrepotDocsList.remove();
      }
      if (this.deleteGroupMode) {
        this.deleteGroupMode = false;
        this.exitSelectionMode();
      }
    },
    selectedRowNumber(item) {
      if (!item) return "";
      const idx = this.displayItems.findIndex((i) => i.id === item.id);
      return idx >= 0 ? idx + 1 : "";
    },
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.selectedItems = [];
        this._selectionSynced = false;
        this.entrepotDocsList.selected = [];
      }
    },
    exitSelectionMode() {
      this.selectionMode = false;
      this.selectedItems = [];
      this._selectionSynced = false;
      this.entrepotDocsList.selected = [];
    },
    isSelected(item) {
      return this.selectedItems.some((i) => i.id === item.id);
    },
    toggleSelect(item) {
      if (this.isSelected(item)) {
        this.selectedItems = this.selectedItems.filter((i) => i.id !== item.id);
      } else {
        this.selectedItems = this.selectedItems.concat([item]);
      }
    },
    selectAllLoaded() {
      const seen = {};
      this.selectedItems.forEach((i) => {
        seen[i.id] = true;
      });
      this.selectedItems = this.selectedItems.concat(
        this.displayItems.filter((i) => !seen[i.id])
      );
    },
    clearSelection() {
      this.selectedItems = [];
    },
    syncSelection() {
      this.entrepotDocsList.selected = this.selectedItems.slice();
      this._selectionSynced = true;
    },
    bulkLock(type) {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.entrepotDocsList.lockGroup(type);
      this.exitSelectionMode();
    },
    copyLink(item) {
      if (!item) return "";
      const type = this.entrepotDocsList.type || "";
      return this.$baseURL + "/entrepot/" + type + "/print/" + item.id;
    },
    onCopyLinkQuick() {
      this.moreSheet = false;
      if (typeof this.entrepotDocsList.copied === "function") {
        this.entrepotDocsList.copied();
      }
    },
    runNamedAction(name) {
      const item = this.detailItem;
      if (!item) return;
      this.moreSheet = false;
      if (name === "lock") {
        this.detailSheet = false;
        this.entrepotDocsList.lock(item, item.lock ? "unlock" : "lock");
        return;
      }
      if (name === "print") {
        this.detailSheet = false;
        this.entrepotDocsList.print(item);
        return;
      }
      if (name === "label") {
        this.detailSheet = false;
        this.entrepotDocsList.labelPrint(item);
        return;
      }
      if (name === "viewDoc") {
        this.detailSheet = false;
        this.entrepotDocsList.viewDocument(item);
        return;
      }
      if (name === "checkout") {
        this.detailSheet = false;
        this.entrepotDocsList.checkout(item);
        return;
      }
      if (name === "copyToInvoice") {
        this.detailSheet = false;
        this.entrepotDocsList.copyToInvoice(item);
        return;
      }
      if (name === "excel") {
        this.detailSheet = false;
        this.entrepotDocsList.exportRowExcel(item);
        return;
      }
      this.detailSheet = false;
      if (name === "edit") this.entrepotDocsList.edit(item);
    },
    detailRows(item) {
      if (!item) return [];
      const rows = [];
      const push = (label, value, kind) =>
        rows.push({
          label,
          value: value == null || value === "" ? "—" : value,
          kind: kind || null,
        });
      push(this.$t("id"), item.id, "band");
      push(this.$t("document-number"), this.docNumber(item), "band");
      if (this.isTransfer && item.type) {
        push(this.$t("type"), this.$t(item.type));
      }
      push(
        this.$t("entrepot"),
        item.entrepot && item.entrepot.name ? item.entrepot.name : null
      );
      push(
        this.$t("branch"),
        item.branch && item.branch.name ? item.branch.name : null
      );
      if (this.isTransfer) {
        push(
          this.$t("recipientEntrepot"),
          item.recipient_entrepot && item.recipient_entrepot.name
            ? item.recipient_entrepot.name
            : null
        );
        push(
          this.$t("recipientBranch"),
          item.recipientBranch && item.recipientBranch.name
            ? item.recipientBranch.name
            : null
        );
      }
      push(this.$t("date"), item.date);
      if (item.status) {
        push(this.$t("statusControll"), this.$t(item.status));
      }
      push(this.$t("description"), item.content);
      const registrar =
        item.user &&
        ((item.user.name) ||
          [item.user.firstname, item.user.lastname].filter(Boolean).join(" "));
      push(this.$t("registerar"), registrar || null);
      if (item.time) push(this.$t("time"), item.time);
      return rows;
    },
  },
};
</script>

<style scoped>
.edlm {
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
  background: #ffffff;
  min-height: calc(100vh - 52px);
  box-sizing: border-box;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}
.edlm--selecting {
  padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px));
}
.edlm__header {
  position: sticky;
  top: 52px;
  z-index: 4;
  background: #ffffff;
  padding: 6px 0 10px;
  border-bottom: 1px solid #eef1f4;
  margin-bottom: 10px;
}
.edlm__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}
.edlm__bar-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 80px;
}
.edlm__bar-btn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.edlm__bar-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.edlm__bar-btn--create {
  background: rgba(91, 124, 153, 0.1);
}
.edlm__bar-btn--back {
  margin-inline-start: auto;
}
.edlm__title {
  flex: 1 1 auto;
  margin: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.edlm__search-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.edlm__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f3f5f7;
}
.edlm__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #37474f;
}
.edlm__search-clear {
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
}
.edlm__filter-btn {
  position: relative;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border: 1px solid #e0e4e8;
}
.edlm__filter-btn:active {
  background: rgba(91, 124, 153, 0.08);
}
.edlm__filter-btn--on {
  border-color: rgba(91, 124, 153, 0.45);
  background: rgba(91, 124, 153, 0.08);
}
.edlm__filter-dot {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.edlm__select-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 4px 0;
}
.edlm__select-bar-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.08);
  font-size: 11px;
  font-weight: 600;
  color: #455a64;
  cursor: pointer;
}
.edlm__select-bar-btn:active {
  background: rgba(91, 124, 153, 0.16);
}
.edlm__select-count {
  flex: 1 1 auto;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.edlm__state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
  color: #607d8b;
  font-size: 13px;
}
.edlm__state--empty {
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(100vh - 220px);
  padding: 32px 16px 24px;
  background: #ffffff;
}
.edlm__state--more {
  padding: 14px 0;
}
.edlm__empty-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.85;
}
.edlm__empty-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
  text-align: center;
}
.edlm__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}
.edlm__sentinel {
  height: 1px;
}
.edlm-card {
  position: relative;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
  transition: transform 0.15s ease;
}
.edlm-card--selected {
  transform: scale(0.99);
  border-color: rgba(21, 101, 192, 0.45);
}
.edlm-card__select-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.edlm-card__select-overlay--on {
  background: rgba(38, 50, 56, 0.12);
}
.edlm-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0d47a1;
  box-shadow: 0 2px 6px rgba(13, 71, 161, 0.4);
}
.edlm-bulkbar {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.14);
}
.edlm-bulkbar__btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
  padding: 6px 2px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.edlm-bulkbar__btn:active {
  background: rgba(91, 124, 153, 0.15);
}
.edlm-bulkbar__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.edlm-bulkbar__btn--danger {
  color: #e53935;
  background: rgba(229, 57, 53, 0.06);
}
.edlm-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.edlm-card__title-block {
  min-width: 0;
  flex: 1 1 auto;
}
.edlm-card__name-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.edlm-card__name-link:disabled {
  cursor: default;
}
.edlm-card__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: right;
}
.edlm-card__sub {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.edlm-card__number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 10px;
  padding: 2px 8px;
}
.edlm-card__amounts {
  margin-top: 10px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.edlm-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.edlm-card__amount-label {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.edlm-card__amount-value {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.edlm-card__status-pill {
  display: inline-flex;
  align-items: center;
  max-width: 62%;
  height: 24px;
  padding: 0 8px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.edlm-card__status-pill--empty {
  background: rgba(144, 164, 174, 0.12);
  color: #90a4ae;
  border-color: transparent;
}
.edlm-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.edlm-card__actions {
  display: flex;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
  flex-wrap: wrap;
}
.edlm-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 34px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 0;
  -webkit-tap-highlight-color: transparent;
}
.edlm-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.edlm-btn--danger {
  border: 1px solid rgba(229, 57, 53, 0.35);
  background: rgba(229, 57, 53, 0.06);
  color: #c62828;
}
.edlm-btn--primary {
  background: #5b7c99;
  color: #ffffff;
}
.edlm-btn--outline {
  background: #ffffff;
  color: #5b7c99;
  border: 1px solid #cfd8dc;
}
.edlm-rel-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.08);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.edlm-rel-btn:active {
  background: rgba(91, 124, 153, 0.16);
}
.edlm-rel-btn:disabled {
  background: transparent;
  color: #90a4ae;
  opacity: 0.55;
  cursor: default;
}
.edlm-rel-empty {
  padding: 18px 0;
  font-size: 12.5px;
  color: #90a4ae;
  text-align: center;
}
.edlm-rel-item {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 10px;
}
.edlm-rel-item__title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
}
.edlm-rel-item__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;
}
.edlm-rel-item__key {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.edlm-rel-item__val {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.edlm-sheet {
  border-radius: 10px 10px 0 0 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.edlm-sheet__handle {
  flex: 0 0 auto;
  width: 44px;
  height: 4px;
  border-radius: 10px;
  background: #cfd8dc;
  margin: 10px auto 2px;
}
.edlm-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 10px;
  border-bottom: 1px solid #eef1f4;
}
.edlm-sheet__header-side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  min-width: 0;
}
.edlm-sheet__header-side--end {
  justify-content: flex-end;
}
.edlm-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.edlm-sheet__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.edlm-sheet__num-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 10px;
  background: rgba(135, 158, 182, 0.14);
  border: 1px solid rgba(135, 158, 182, 0.28);
  color: #5b7c99;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.edlm-sheet__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.edlm-sheet__back:active {
  background: rgba(91, 124, 153, 0.1);
}
.edlm-sheet__quickbar {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
}
.edlm-sheet__quick {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 46px;
  padding: 4px 2px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.edlm-sheet__quick:disabled {
  opacity: 0.45;
  cursor: default;
}
.edlm-sheet__quick:active:not(:disabled) {
  background: rgba(91, 124, 153, 0.14);
}
.edlm-sheet__name-block {
  flex: 0 0 auto;
  background: #edf2f7;
  border-radius: 10px;
  padding: 10px 14px;
  margin: 6px 16px 0;
}
.edlm-sheet__name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.edlm-sheet__name-value {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 70%;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.edlm-sheet__name-block .edlm-sheet__linkrow {
  margin: 8px 0 0;
  background: rgba(255, 255, 255, 0.75);
}
.edlm-sheet__body {
  overflow-y: auto;
  padding: 8px 16px 20px;
}
.edlm-sheet__card {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 4px 14px;
}
.edlm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
}
.edlm-sheet__card .edlm-sheet__row:last-child {
  border-bottom: 0;
}
.edlm-sheet__row--band {
  margin: 0 -14px;
  padding: 8px 14px;
  border-bottom-color: rgba(91, 124, 153, 0.12);
  background: rgba(91, 124, 153, 0.05);
}
.edlm-sheet__row-label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.edlm-sheet__row-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.edlm-sheet__tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
  max-width: 62%;
}
.edlm-sheet__linkrow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  margin: 6px 0 2px;
  padding: 0 10px;
  border: 1px solid rgba(0, 150, 136, 0.16);
  border-radius: 10px;
  background: rgba(0, 150, 136, 0.07);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.edlm-sheet__linkrow-text {
  flex: 1 1 auto;
  font-size: 12.5px;
  font-weight: 700;
  color: #00796b;
  text-align: right;
}
.edlm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.edlm-sheet__actions--sheet {
  padding-top: 4px;
}
.edlm-sheet__action {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 42px;
  padding: 0 6px;
  border: 0;
  border-bottom: 1px solid #e8edf2;
  border-radius: 10px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.edlm-sheet__action:disabled {
  opacity: 0.45;
  cursor: default;
}
.edlm-sheet__action:active:not(:disabled) {
  background: rgba(91, 124, 153, 0.08);
}
.edlm-sheet__action--danger {
  color: #c62828;
}
.edlm-sheet__action--log {
  cursor: default;
  padding: 0;
}
.edlm-sheet__action--log > div {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.edlm-sheet__action--log ::v-deep .v-list-item {
  flex: 1 1 auto;
  min-height: 42px;
  height: 42px;
  padding: 0;
}
.edlm-sheet__action--log ::v-deep .v-list-item__icon {
  margin: 0 !important;
  min-width: 19px;
  align-self: center;
}
.edlm-sheet__action--log ::v-deep .v-list-item__icon .v-icon {
  font-size: 19px !important;
  color: #5b7c99 !important;
}
.edlm-sheet__action--log ::v-deep .v-list-item__title {
  margin-inline-start: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
}
.edlm-sheet--filter {
  max-height: 92vh;
  border-radius: 20px 20px 0 0 !important;
}
.edlm-sheet__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef1f4;
  background: #ffffff;
}
.edlm-sheet__btn {
  flex: 1 1 0;
  height: 44px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.edlm-sheet__btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.edlm-sheet__btn--primary:active {
  background: #4d6a84;
}
.edlm-sheet__btn--ghost {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #546e7a;
}
.edlm-sheet__btn--ghost:active {
  background: rgba(91, 124, 153, 0.08);
}
.edlm-filter-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #ffffff;
}
.edlm-filter-nav__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.edlm-filter-nav__item:active {
  background: #f7f9fc;
}
.edlm-filter-nav__item:last-child {
  border-bottom: 0;
}
.edlm-filter-nav__icon {
  flex: 0 0 auto;
}
.edlm-filter-nav__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.edlm-filter-nav__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}
.edlm-filter-nav__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.edlm-filter-nav__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.edlm-filter-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px 6px;
  background: #ffffff;
}
.edlm-filter-fields > * {
  margin-bottom: 14px;
}
.edlm-picker-host {
  display: none !important;
}
.edlm-picker-overlay {
  position: relative;
  height: 40px;
  overflow: hidden;
  border-radius: 10px;
}
.edlm-picker-cover {
  position: absolute !important;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
}
.edlm-picker-cover__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}
.edlm-f-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.edlm-f-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 4px;
}
.edlm-f-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.edlm-f-picker-field:active {
  background: #eef4f8;
  border-color: #90caf9;
}
.edlm-f-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.edlm-select-layer {
  padding: 6px 16px 20px;
}
.edlm-select-layer__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.edlm-select-layer__text {
  font-size: 13.5px;
  font-weight: 500;
  color: #37474f;
}
.edlm-select-layer__clear {
  display: block;
  width: 100%;
  padding: 14px 4px 4px;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #c62828;
  text-align: center;
  cursor: pointer;
}
.edlm-mobile-date {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
  background: #ffffff;
}
.edlm-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #f7f9fc;
}
.edlm-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.edlm-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
}
.edlm-mobile-date__clear {
  border: 0;
  background: transparent;
  color: #e91e63;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
}
.edlm-mobile-date__picker {
  width: 100%;
}
.edlm-mobile-date__picker ::v-deep .vpd-main,
.edlm-mobile-date__picker ::v-deep .vpd-wrapper,
.edlm-mobile-date__picker ::v-deep .vpd-container,
.edlm-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
}
.edlm-confirm-sheet {
  border-radius: 20px 20px 0 0 !important;
  padding: 8px 16px 16px;
  background: #fff !important;
}
.edlm-confirm-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 14px;
  border-radius: 999px;
  background: #cfd8dc;
}
.edlm-confirm-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}
.edlm-confirm-sheet__title {
  text-align: center;
  font-size: 16px;
  font-weight: 800;
  color: #263238;
  margin-bottom: 4px;
}
.edlm-confirm-sheet__subtitle {
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: #78909c;
  margin: 0 0 14px;
}
.edlm-confirm-sheet__rows {
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 4px 12px;
  margin-bottom: 14px;
}
.edlm-confirm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
  font-size: 13px;
  color: #546e7a;
}
.edlm-confirm-sheet__row:last-child {
  border-bottom: 0;
}
.edlm-confirm-sheet__row strong {
  color: #263238;
  font-weight: 700;
  text-align: left;
}
.edlm-confirm-sheet__items {
  max-height: 160px;
  overflow-y: auto;
  padding: 4px 0 8px;
}
.edlm-confirm-sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px dashed #e0e4e8;
}
.edlm-confirm-sheet__item:last-child {
  border-bottom: 0;
}
.edlm-confirm-sheet__item-main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.edlm-confirm-sheet__item-index {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(91, 124, 153, 0.12);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.edlm-confirm-sheet__item-name {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.edlm-confirm-sheet__item-number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.edlm-confirm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.edlm-confirm-sheet__yes {
  border-radius: 10px !important;
}
.edlm-confirm-sheet__no {
  border-radius: 10px !important;
}
</style>
